import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, ValidatorFn } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Disposable } from "@app/modules/common";
import { AppQuery, LoginStatus } from "@app/modules/common/app.store";
import {
  ProductSettingService,
  StockService
} from "@app/modules/common/services";
import {
  amount,
  codeOnly,
  JobDto,
  JobLineDto,
  JobStatusGrouping,
  newJobLine,
  ResourceMasterDto,
  StockMasterDto
} from "@shared/models";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { DetailsComponent } from "../details/details.component";
import {
  LineComponentData,
  LineModalComponent
} from "../line-modal/line-modal.component";

@Component({
  selector: "abi-line",
  templateUrl: "./line.component.html",
  styleUrls: ["./line.component.scss"]
})
export class LineComponent extends Disposable implements OnChanges {
  public readonly sortMap = ["LAB", "TRV", "STK", "CON"];
  paymentTypes = ["Pre", "Inv"];
  minLineCount = 3;
  lines: JobLineDto[];
  lineTypeIdClass: Observable<string>;
  isMobile: Observable<boolean>;
  showDelivered = false;
  baseTravelMode = "fixed";
  baseLabourMode = "fixed";
  lineModal: MatDialogRef<LineModalComponent>;
  exchangeMode = false;

  @ViewChild("paidDialog") paidDialog: any;
  @Input() parent: DetailsComponent;
  @Input() job: JobDto;
  @Input() resource: ResourceMasterDto;
  @Input() payment: FormGroup;
  @Input() codJob: boolean;
  @Input() isReadonly: boolean;
  @Input() form: FormGroup;
  @Input() formErrors: { [control: string]: Array<string> };
  @Output() newLines: EventEmitter<JobLineDto[]> = new EventEmitter();
  @Output() saveJob: EventEmitter<boolean> = new EventEmitter();
  @Input() stockParentId: string;// Could be a modelId, job type, diag code, anything

  get jobLines(): FormArray {
    return this.form.get('jobLines') as FormArray;
  }

  showPaymentTypes = false;
  constructor(
    private productSettings: ProductSettingService,
    private formBuilder: FormBuilder,
    private dialog: MatDialog,
    responsive: BreakpointObserver,
    private stockService: StockService,
    private appQuery: AppQuery
  ) {
    super();
    this.showPaymentTypes = productSettings.booleanValue("JobShowPaymentTypes");
    this.showDelivered = productSettings.booleanValue("JobShowDelivered"); // TODO: Provide Base Settings (like Translations)
    this.isMobile = responsive
      .observe(Breakpoints.HandsetPortrait)
      .pipe(map(o => o.matches));
    this.lineTypeIdClass = this.isMobile.pipe(map(s => (s ? "w-50" : "w-30")));
    this.appQuery.$roles.pipe(this.notDisposed()).subscribe();
  }

  get canChangePayment() {
    return this.appQuery.loginStatus === LoginStatus.True
    && this.appQuery.hasRoles(["SUPER", "MAN"])
    && this.codJob
    && this.showPaymentTypes;
  }

  get canMarkPaid(){
    return this.appQuery.loginStatus === LoginStatus.True
    && this.appQuery.hasRoles(["FINANCE"])
    && !JobStatusGrouping.CANCELLED.includes(this.job.statusId);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.job || changes.form)
      && this.job
      && this.form
    ) {
      this.setupJobLines();
    }
    if(changes.isReadonly) {
      this.setupJobLines();
    }
  }

  get totalAmount(): number {
    if (this.jobLines) {
      const lines = this.jobLines?.getRawValue();
      return lines.reduce(
        (t: number, line) => t + line.quantityOrdered * line.price,
        0
      );
    }
    return 0;
  }

  markPaid() {
    this.payment.enable();
    const oldValue = this.payment.value;
    const ref = this.dialog.open(this.paidDialog);
    ref.afterClosed().subscribe((result: boolean) => {
      if (!result) {
        this.payment.reset(oldValue);
        this.payment.disable();
      } else {
        let allocationRemainder = this.payment.value.paid as number;
        const pmtDate = Date.today().format("yMMdd");
        const receipt = this.payment.value.receipt;// Payment Name
        // Move Credits to Top of List
        const sortedLines = this.job.lines.sort((a,b) => {
          if(a.price < b.price) {
            return -1;
          } else if(a.price > b.price) {
            return 1;
          } else {
            return 0;
          }
        });
        const linesIterator = sortedLines.values();
        let iteratedLine = linesIterator.next();
        while (!iteratedLine.done) {
          const line = iteratedLine.value;

          const lineOutstandingAmount = line.price * (1 - line.discount) * line.quantityOrdered - line.paid;
          if (!line.receipt || lineOutstandingAmount ) {
            // Mark Negative Lines as Paid
            if(line.price < 0) {
              line.paid = line.price * line.quantityOrdered;
            } else {
              line.paid = line.paid + Math.min(lineOutstandingAmount, allocationRemainder);
            }
            // Skip 0 payment lines
            if (line.paid || line.paid < 0) {
              line.fields[3] = pmtDate;
              line.receipt = receipt;
              line.requested = Date.today();
            }
            allocationRemainder = Math.max(0, allocationRemainder - lineOutstandingAmount);
          }
          iteratedLine = linesIterator.next();
        }

        // console.log("Marking Job as Paid", this.job.lines);
        this.saveJob.emit(true);
      }
    });
  }

  public codeOnly(item: any): string {
    return codeOnly(item);
  }

  baseTravel(lineGroup: FormGroup | JobLineDto): boolean {
    const line =
      lineGroup instanceof FormGroup ? lineGroup.getRawValue() : lineGroup;
    const showTravel = this.productSettings.booleanValue(
      "AllowExtraTravelLines"
    );
    return (
      !this.isReadonly && line.typeId === "TRV" && (!line.adhoc || !showTravel)
    );
  }

  baseLabour(lineGroup: FormGroup | JobLineDto): boolean {
    const line =
      lineGroup instanceof FormGroup ? lineGroup.getRawValue() : lineGroup;
    const showLabour = this.productSettings.booleanValue(
      "AllowExtraLabourLines"
    );
    return (
      !this.isReadonly && line.typeId === "LAB" && (!line.adhoc || !showLabour)
    );
  }

  private createJobLineGroup(quantity: any, line: JobLineDto = null) {
    const item = this.formBuilder.group({
      lineId: "NEW",
      typeId: "STK",
      itemId: "",
      line1: "",
      quantityOrdered: quantity,
      quantityDelivered: 0,
      amount: "0.00",
      price: 0,
      cost: 0,
      mode: ["", this.createCustomModeValidator(this.canChangePayment)],
      adhoc: true,
      vatId: "",
      vatRate: 0,
      resourceId: "",
    });
    if (line) {
      item.reset({
        lineId: line.lineId,
        typeId: line.typeId,
        itemId: line.itemId,
        line1: line.line1,
        quantityOrdered: line.quantityOrdered ? line.quantityOrdered : "",
        quantityDelivered: line.quantityDelivered ? line.quantityDelivered : "",
        price: line.price,
        cost: line.cost,
        mode: line.mode,
        amount: amount(line).toFixed(2),
        adhoc: line.adhoc,
        vatId: line.vatId,
        vatRate: line.vatRate,
        resourceId: line.resourceId,
      });
      const priceField = item.get("price");
      if (line.typeId !== "CON") {
        priceField.disable();
      }
    }

    item.get("amount").disable();

    if (this.isReadonly || (line && line.receipt)) {
      item.disable();
      // if itsReadonly - never enable a field
      if (!line?.quantityDelivered && !this.isReadonly) {
        item.get("quantityDelivered").enable();
      }
    } else {
      item.valueChanges
        .pipe(this.notDisposed())
        .subscribe(v => this.jobLineChanged(item, v));
      item
        .get("typeId")
        .valueChanges.pipe(this.notDisposed())
        .subscribe(v => this.jobLineTypeChanged(item, v));
    }
    return item;
  }

  private createLabourLine(): JobLineDto {
    const labourLine = newJobLine("");
    labourLine.lineId = "NEW";
    labourLine.itemId = "";
    labourLine.line1 = "";
    labourLine.quantityOrdered = 0;
    labourLine.quantityDelivered = 0;
    labourLine.typeId = "LAB";
    labourLine.price = 0;
    labourLine.cost = 0;
    labourLine.mode = "Pre";
    return labourLine;
  }

  private createTravelLine(): JobLineDto {
    const travelLine = newJobLine("");
    travelLine.lineId = "NEW";
    // travelLine.itemId = trvStock.code;
    // travelLine.line1 = trvStock.description;
    travelLine.quantityOrdered = 0;
    travelLine.quantityDelivered = 0;
    travelLine.typeId = "TRV";
    travelLine.price = 0;
    travelLine.cost = 0;
    travelLine.mode = "";
    return travelLine;
  }

  public setupJobLines() {
    this.exchangeMode = this.productSettings.stringValue("ExcludeJobStatusBooked").includes(this.job.statusId);

    this.lines = [...this.job.lines];// Copy of Lines
    const showTravel = this.productSettings.booleanValue(
      "AllowExtraTravelLines"
    );
    const showLabour = this.productSettings.booleanValue(
      "AllowExtraLabourLines"
    );
    this.baseTravelMode = showTravel ? "fixed" : "all";
    this.baseLabourMode = showLabour ? "fixed" : "all";

    const jobLines = [// Copy of Lines
      ...this.job.lines
    ]; /*.filter(
      l => this.isReadonly() || l.typeId === "STK" || l.typeId === "CON" || l.typeId === "TRV" || l.typeId === "LAB");*/

    jobLines.sort((a, b) => {
      const type =
        this.sortMap.indexOf(a.typeId) - this.sortMap.indexOf(b.typeId);
      const ba = this.baseLabour(a) || this.baseLabour(a) ? 0 : 1;
      const bb = this.baseLabour(b) || this.baseLabour(b) ? 0 : 1;
      const base = type !== 0 ? type : ba - bb;
      return base !== 0 ? base : Number(a.lineId) - Number(b.lineId);
    });

    let newLabAdded = false;
    if (!this.isReadonly) {// Is editable - so add Labour and Travel Lines - if applicable
      let labourLine = this.job.lines.find(j => this.baseLabour(j));
      const numberOfExtraLabourLines = this.productSettings.numericValue("AllowExtraLabourLines");
      const showLab =
        !!numberOfExtraLabourLines;
      if (!labourLine && showLab) {
        labourLine = this.createLabourLine();
        newLabAdded = true;
        if (labourLine) {
          jobLines.splice(0, 0, labourLine);
        }
      }
      // Allow second Labour Line to Added (limited by Product Setting)
      if (
        this.exchangeMode
        && !newLabAdded
        && showLab
        && this.job.lines.filter(j => this.baseLabour(j)).length < (numberOfExtraLabourLines + 1)
      ) {
        labourLine = this.createLabourLine();
        if (labourLine) {
          jobLines.splice(0, 0, labourLine);
        }
      }


      const numberOfExtraTravelLines = this.productSettings.numericValue("AllowExtraTravelLines");
      const showTrv = !!numberOfExtraTravelLines;
      if (!showTrv) {
        this.minLineCount--;
      }
      let travelLine = this.job.lines.find(line => this.baseTravel(line));

      let newTravelAdded = false;
      // && (!line.itemId /*|| this.travelStock.containsKey(codeOnly(line.itemId))*/));
      if (!travelLine && (showTrv || showTravel)) {
        travelLine = this.createTravelLine();
        newTravelAdded = true;
        if (travelLine) {
          jobLines.splice(1, 0, travelLine);
        }
      }

      // Allow second Travel Line to be Added (limited by Product Setting)
      if (
        this.exchangeMode
        && !newTravelAdded
        && showTrv
        && this.job.lines.filter(j => this.baseTravel(j)).length < (numberOfExtraTravelLines + 1)
      ) {
        travelLine = this.createTravelLine();
        if (travelLine) {
          jobLines.splice(1, 0, travelLine);
        }
      }
    }

    // populate the formArray
    (this.form.get('jobLines') as FormArray).clear();
    // this.jobLines.push(this.createJobLineGroup(0));
    // this.form.setControl(
    //   "jobLines",
    //   this.formBuilder
    //   .array(jobLines.map(j => this.createJobLineGroup(j.quantityOrdered, j)))
    // );
    jobLines.forEach((line, index) => {
      (this.form.get('jobLines') as FormArray).push( this.createJobLineGroup(line.quantityOrdered, line));
    });

    if (!this.isReadonly) {
      this.addJobLine(0);
    }
  }

  private jobLineTypeChanged(line: FormGroup, value: any) {
    line.patchValue({
      itemId: "",
      quantity: 1,
      price: "0.00",
      cost: 0,
      mode: "",
      line1: ""
    });
  }

  private jobLineChanged(line: FormGroup, value: any) {
    value = line.getRawValue();
    const item = line.get("itemId");
    if (!item) {
      return;
    } /*
    if (line === this.labourLine) {
      if (!value.quantityOrdered && item.enabled) {
        //setTimeout(() => item.disable(), 1);
        return;
      } else if (value.quantityOrdered && item.disabled) {
        //setTimeout(() => item.enable(), 1);
        return;
      }
    }*/
    /*if (codeOnly(value.typeId) === "STK" && line.get("price").enabled) {
      line.get("price").disable();
      return;
    } else if (codeOnly(value.typeId) === "CON" && line.get("price").disabled) {
      line.get("price").enable();
      return;
    }*/
    const amt = (value.price * value.quantityOrdered).toFixed(2);
    if (/*"amount" i value && */ value.amount !== amt) {
      line.get("amount").setValue(amt, { emitEvent: false });
    }
  }

  canEditLine(line: FormGroup) {
    return (
      /*(typeId === "STK" || typeId === "CON") && */ !this.isReadonly &&
      line.enabled
    );
  }

  public addJobLine(quantity: any) {
    this.jobLines.push(this.createJobLineGroup(quantity));
  }

  public deleteJobLine(index: number) {
    this.jobLines.removeAt(index);
    this.jobLines.markAsDirty();
    this.lineModal.close();
  }

  editJobLine(line: FormGroup, index: number) {
    // eslint-disable-next-line max-len
    // sometimes we must limit stock by warehouse - use prod. settings to check if we must use the warehouse and pass it into the dialog for filtering
    const useWarehouse = this.productSettings.booleanValue("JobLineUseWarehouse") || false;
    const newLine = !line.getRawValue().line1;
    const dlgData: LineComponentData = {
      paymentTypes: this.paymentTypes,
      canChangePayment: this.canChangePayment,
      errors: this.formErrors,
      group: line,
      jobId: this.job.jobId,
      parentId: this.stockParentId,
      warehouseId: useWarehouse && this.resource ? this.resource.warehouse : "",
      onDelete:
        this.jobLines.length > this.minLineCount && line.getRawValue().line1
          ? l => {
              this.deleteJobLine(index);
              return true;
            }
          : () => null
    };
    this.lineModal = this.dialog.open(LineModalComponent, { data: dlgData });
    this.lineModal.backdropClick().subscribe(() => {
      this.lineModal.componentInstance.cancel();
    });
    this.lineModal.afterClosed()
    .subscribe((result: boolean) => {
      if (result) {
        line.patchValue({ ...line.getRawValue()});// This forces a re-evaluation of the formGroup (and displays the mode value in the select)
        if (!line.value.resourceId)
          line.patchValue({resourceId: this.job.resourceId});
        if (newLine) {
          this.addJobLine(0);
        }
      }
    });
    return false;
  }

  showStock(stock: StockMasterDto, line: FormGroup) {
    this.stockService
      .getPrice(stock.code, this.job.jobId)
      .toPromise()
      .then(data =>
        line.patchValue({
          itemId: stock.code,
          line1: stock.description,
          price: data.price,
          amount: data.price,
          cost: data.price,
          mode: stock.mode,
          vatId: stock.vatId,
          vatRate: stock.vatId ? 0.15 : 0,// CODE SMELL: magic numbers
          resourceId: this.job.resourceId,
        })
      );
  }

  lineDescription1(grp: FormGroup): string {
    return grp.getRawValue().line1;
  }

  toggleLabour(line: FormGroup) {
    line.patchValue({
      quantityOrdered: !line.getRawValue().quantityOrdered
    });
    line.markAsDirty();
  }

  deliverLine(line: FormGroup) {
    const value = line.getRawValue();
    const del = !!value.quantityDelivered;
    line.patchValue({
      quantityDelivered: del ? 0 : +value.quantityOrdered
    });
    line.markAsDirty();
  }

  public lineClaims(): string[] {
    return this.sortMap.map(s => "JobLineShow" + s + "Price");
  }

  private createCustomModeValidator(canChangePayment: boolean): ValidatorFn {
    return (control: FormControl) => {
      if (!control.parent) {
        return null;
      }
      if (!canChangePayment) {
        return null;
      }

      if(!control.value) {
        const a = control.parent.get("itemId").value;
        if(!!a)
          return { required: true };
      }
      return null;
    };
  }
}
